import type { Header, Table } from '@tanstack/react-table';

import {
  MOBILE_TABLE_META_COLUMN_ID,
  MOBILE_TABLE_RECORD_ID_COLUMN_ID,
  MOBILE_TABLE_UTILITY_COLUMN_ID,
} from './mobile-table-cards.types';

export function isMobileTableUtilityColumn(columnId: string): boolean {
  try {
    const normalizedId = columnId.toLowerCase();
    return (
      normalizedId === MOBILE_TABLE_UTILITY_COLUMN_ID.SELECT ||
      normalizedId === MOBILE_TABLE_UTILITY_COLUMN_ID.ACTION ||
      normalizedId === MOBILE_TABLE_UTILITY_COLUMN_ID.ACTIONS
    );
  } catch (error) {
    console.error('Failed to classify table column for mobile cards', {
      columnId,
      error,
    });
    throw error;
  }
}

export function formatMobileTableColumnLabel(columnId: string): string {
  try {
    if (!columnId || columnId === 'id') {
      return '';
    }

    return columnId
      .replace(/[_-]/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  } catch (error) {
    console.error('Failed to format mobile table column label', {
      columnId,
      error,
    });
    throw error;
  }
}

export function shouldOmitMobileTableCellValue(value: unknown): boolean {
  try {
    if (value == null) {
      return true;
    }

    if (typeof value === 'string' && value.trim() === '') {
      return true;
    }

    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('Failed to decide whether to hide a mobile table cell', {
      error,
    });
    throw error;
  }
}

export function isMobileTableRowNumberColumn(columnId: string): boolean {
  try {
    const normalizedId = columnId.toLowerCase().replace(/[_-\s]/g, '');
    return (
      normalizedId === MOBILE_TABLE_META_COLUMN_ID.ROW_NUMBER ||
      columnId === '#'
    );
  } catch (error) {
    console.error('Failed to classify row number column for mobile cards', {
      columnId,
      error,
    });
    throw error;
  }
}

export function isMobileTableRecordIdColumn(columnId: string): boolean {
  try {
    // Keep underscores so `_id` accessors used for actions/balance stay visible.
    const normalizedId = columnId.toLowerCase().replace(/[-\s]/g, '');

    switch (normalizedId) {
      case MOBILE_TABLE_RECORD_ID_COLUMN_ID.ID:
      case MOBILE_TABLE_RECORD_ID_COLUMN_ID.INVOICE_ID:
      case MOBILE_TABLE_RECORD_ID_COLUMN_ID.RECORD_ID:
        return true;
      default:
        return false;
    }
  } catch (error) {
    console.error('Failed to classify record id column for mobile cards', {
      columnId,
      error,
    });
    throw error;
  }
}

export function pickMobileTableTitleColumnId(
  columnIds: Array<string>,
): string | undefined {
  try {
    return columnIds.find(
      (columnId) =>
        !isMobileTableRowNumberColumn(columnId) &&
        !isMobileTableRecordIdColumn(columnId),
    );
  } catch (error) {
    console.error('Failed to pick mobile table title column', {
      columnIds,
      error,
    });
    throw error;
  }
}

export function shouldShowMobileTableDetailCell(
  columnId: string,
  value: unknown,
): boolean {
  try {
    if (isMobileTableUtilityColumn(columnId)) {
      return false;
    }

    if (isMobileTableRowNumberColumn(columnId)) {
      return true;
    }

    if (isMobileTableRecordIdColumn(columnId)) {
      return false;
    }

    return !shouldOmitMobileTableCellValue(value);
  } catch (error) {
    console.error('Failed to decide whether to show a mobile table detail', {
      columnId,
      error,
    });
    throw error;
  }
}

export function getMobileTableSelectHeader<TData>(
  table: Table<TData>,
): Header<TData, unknown> | undefined {
  try {
    for (const headerGroup of table.getHeaderGroups()) {
      const header = headerGroup.headers.find((item) => {
        return (
          item.column.id.toLowerCase() === MOBILE_TABLE_UTILITY_COLUMN_ID.SELECT
        );
      });
      if (header) {
        return header;
      }
    }

    return undefined;
  } catch (error) {
    console.error('Failed to find the mobile table select-all header', {
      error,
    });
    throw error;
  }
}
